import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RoutePagesSection from "@/components/RoutePagesSection";
import { 
  Phone, Mail, MapPin, Car, Shield, Clock, Users, Star, 
  CheckCircle, ArrowRight, Plane, Calendar, CreditCard,
  Menu, X, ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Location Search Component
function LocationSearch({ placeholder, onSelect, value, icon: Icon, iconColor }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false);

  useEffect(() => {
    if (selected || query.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: { q: query, format: 'json', addressdetails: 1, countrycodes: 'gb', limit: 5 }
        });
        setSuggestions(res.data);
        setShowSuggestions(true);
      } catch (e) { console.error(e); }
      setLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, selected]);

  const handleSelect = (place) => {
    setQuery(place.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelected(true);
    onSelect({ address: place.display_name, lat: parseFloat(place.lat), lng: parseFloat(place.lon) });
  };

  return (
    <div className="relative">
      <div className="flex items-center bg-white rounded-lg border-2 border-slate-200 focus-within:border-orange-500 transition-colors">
        <div className={`pl-4 ${iconColor}`}><Icon size={20} /></div>
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(false); }}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="w-full px-3 py-4 bg-transparent outline-none text-slate-800 placeholder-slate-400"
        />
        {loading && <div className="pr-4"><div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div></div>}
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
          {suggestions.map((place, i) => (
            <div key={i} onClick={() => handleSelect(place)} className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-0">
              <MapPin size={16} className="text-orange-500 flex-shrink-0" />
              <span className="text-sm text-slate-700 line-clamp-1">{place.display_name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main HomePage Component
export default function HomePage() {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState({ address: "", lat: null, lng: null });
  const [dropoff, setDropoff] = useState({ address: "", lat: null, lng: null });

  const handleBooking = () => {
    if (!pickup.lat || !dropoff.lat) { alert("Please select pickup and dropoff locations"); return; }
    navigate('/book', { state: { pickupLocation: pickup, dropoffLocation: dropoff } });
  };

  const stats = [
    { number: "15+", label: "Years Experience" },
    { number: "50K+", label: "Happy Customers" },
    { number: "100+", label: "Professional Drivers" },
    { number: "24/7", label: "Service Available" }
  ];

  const features = [
    { icon: Shield, title: "Safe & Secure", desc: "All drivers are licensed, vetted and trained to the highest standards" },
    { icon: Clock, title: "Always On Time", desc: "We monitor flights and adjust pickup times automatically" },
    { icon: CreditCard, title: "Fixed Prices", desc: "No hidden charges, no surge pricing. Pay what you see" },
    { icon: Car, title: "Premium Fleet", desc: "Travel in comfort with our modern, well-maintained vehicles" },
    { icon: Users, title: "Meet & Greet", desc: "Your driver will meet you at arrivals with a name board" },
    { icon: Plane, title: "Flight Tracking", desc: "We track your flight and adjust for delays automatically" }
  ];

  const services = [
    { title: "Airport Transfers", desc: "Reliable transfers to all major UK airports", img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600" },
    { title: "Corporate Travel", desc: "Professional chauffeur service for business", img: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=600" },
    { title: "Long Distance", desc: "Comfortable journeys across the UK", img: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600" }
  ];

  const airports = [
    { name: "Heathrow Airport", code: "LHR", from: "45" },
    { name: "Gatwick Airport", code: "LGW", from: "55" },
    { name: "Stansted Airport", code: "STN", from: "50" },
    { name: "Luton Airport", code: "LTN", from: "40" },
    { name: "London City", code: "LCY", from: "35" },
    { name: "Birmingham", code: "BHX", from: "95" }
  ];

  const testimonials = [
    { name: "James Wilson", role: "Business Traveler", text: "Exceptional service! The driver was waiting for me despite my flight being delayed. Will definitely use again.", rating: 5 },
    { name: "Sarah Thompson", role: "Family Trip", text: "Booked for our family holiday. Clean car, friendly driver, and the child seats were already fitted. Perfect!", rating: 5 },
    { name: "Michael Brown", role: "Regular Customer", text: "I use Roman Cars weekly for airport runs. Consistently reliable and professional. Highly recommended.", rating: 5 }
  ];

  const steps = [
    { num: "01", title: "Book Online", desc: "Enter your pickup and destination, choose your vehicle" },
    { num: "02", title: "Confirm & Pay", desc: "Review your booking details and pay securely online" },
    { num: "03", title: "Meet Your Driver", desc: "Your driver will be waiting at the agreed pickup point" },
    { num: "04", title: "Enjoy Your Ride", desc: "Sit back, relax and enjoy the journey" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-[#1a1a2e] overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1920" alt="City" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a2e] via-[#1a1a2e]/90 to-[#1a1a2e]/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star size={16} className="fill-orange-400" /> Rated 4.9/5 by 10,000+ customers
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Premium Airport <span className="text-orange-500">Taxi Service</span> in the UK
              </h1>
              <p className="text-xl text-slate-300 mb-8 max-w-lg">
                Professional, reliable airport transfers with fixed prices. Book your ride in under 60 seconds.
              </p>
              <div className="flex flex-wrap gap-6 text-slate-300">
                <div className="flex items-center gap-2"><CheckCircle size={20} className="text-green-500" /> Free cancellation</div>
                <div className="flex items-center gap-2"><CheckCircle size={20} className="text-green-500" /> Flight monitoring</div>
                <div className="flex items-center gap-2"><CheckCircle size={20} className="text-green-500" /> Meet & greet</div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Book Your Transfer</h2>
              <p className="text-slate-500 mb-6">Get an instant quote for your journey</p>
              <div className="space-y-4">
                <LocationSearch placeholder="Pickup location" onSelect={setPickup} value={pickup.address} icon={MapPin} iconColor="text-green-500" />
                <LocationSearch placeholder="Dropoff location" onSelect={setDropoff} value={dropoff.address} icon={MapPin} iconColor="text-red-500" />
                <button onClick={handleBooking} className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg shadow-lg shadow-orange-500/30 transition-all flex items-center justify-center gap-2">
                  Get Quote <ArrowRight size={20} />
                </button>
              </div>
              <p className="text-center text-sm text-slate-400 mt-4">No payment required to get a quote</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-bold text-orange-500 mb-1">{stat.number}</div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why Choose Roman Cars?</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">We provide premium airport transfer services with a focus on reliability, comfort and customer satisfaction.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-slate-100">
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon size={28} className="text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600">Professional transportation solutions for every need</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, i) => (
              <div key={i} className="group relative rounded-2xl overflow-hidden shadow-xl">
                <img src={service.img} alt={service.title} className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-slate-300 mb-4">{service.desc}</p>
                  <Link to="/book" className="inline-flex items-center gap-2 text-orange-400 font-semibold hover:text-orange-300">
                    Book Now <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Airport Routes */}
      <section className="py-20 bg-[#1a1a2e]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Popular Airport Routes</h2>
            <p className="text-lg text-slate-400">Fixed prices from London to major UK airports</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {airports.map((airport, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 hover:bg-white/20 transition-colors border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                      <Plane size={24} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{airport.name}</h3>
                      <p className="text-sm text-slate-400">{airport.code}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-slate-400">From London</div>
                  <div className="text-2xl font-bold text-orange-500">£{airport.from}</div>
                </div>
                <Link to="/book" className="mt-4 block w-full py-2 text-center border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-500 hover:text-white transition-colors font-medium">
                  Book This Route
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Book your airport transfer in 4 simple steps</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-orange-500">{step.num}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-slate-600">Trusted by thousands of travelers across the UK</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={20} className={j < t.rating ? "text-yellow-400 fill-yellow-400" : "text-slate-200"} />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{t.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-500 font-bold">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{t.name}</p>
                    <p className="text-sm text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Book Your Transfer?</h2>
          <p className="text-xl text-orange-100 mb-8">Get a free quote in seconds. No hidden fees, no obligations.</p>
          <Link to="/book" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-500 font-bold rounded-lg hover:bg-slate-100 transition-colors shadow-xl">
            Book Now <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <RoutePagesSection />

      <Footer />
    </div>
  );
}
